import UtilityService from "./UtilityService";

const SkillTextService = {
	/**
	 * The blocks of a skill's text without a leading heading that only repeats the page title,
	 * so a page that already shows the name does not show it twice. The text itself is untouched;
	 * an agent still reads the heading.
	 *
	 * @param {Object} params
	 * @param {Array} params.blocks from readableBlocksOfSkill
	 * @param {String} params.pageTitle
	 * @return {Array}
	 */
	blocksWithoutRepeatedTitle({ blocks, pageTitle }) {
		const firstBlock = blocks[0];
		if (!firstBlock || firstBlock.kind !== "heading") return blocks;
		const headingText = firstBlock.spans.map(span => span.text).join("");
		const isRepeatedTitle = UtilityService.toSearchText(headingText) === UtilityService.toSearchText(pageTitle);
		const shownBlocks = isRepeatedTitle ? blocks.slice(1) : blocks;
		return shownBlocks;
	},

	/**
	 * A skill's text as blocks a screen can lay out without reading it as HTML: headings ("#"),
	 * bullet lines ("-" or "*"), numbered lines ("1." or "1)", keeping the number they start at),
	 * fenced text ("```") kept as written, and paragraphs. A line under a list item that is
	 * indented or lettered ("a)", "i.", "1.1") is a detail of that item, not a new paragraph, so a
	 * numbered list stays one list. Inside headings, bullets and paragraphs, text between "**"
	 * pairs is bold.
	 *
	 * @param {Object} params
	 * @param {String} params.body
	 * @return {Array} [{ kind: heading | bullets | numbered | fenced | paragraph, level, spans, items: [{ spans, details: [spans] }], start, text }]
	 */
	readableBlocksOfSkill({ body }) {
		const lines = String(body || "").split("\n");
		const blocks = [];
		let fencedLines = null;
		let paragraphLines = [];

		const closeParagraph = () => {
			if (paragraphLines.length === 0) return;
			const paragraphText = paragraphLines.join(" ");
			blocks.push({ kind: "paragraph", spans: this.boldSpansOf({ text: paragraphText }) });
			paragraphLines = [];
		};

		const addListItem = ({ kind, itemText, number }) => {
			const previousBlock = blocks[blocks.length - 1];
			const isContinuingList = !!previousBlock && previousBlock.kind === kind;
			const item = { spans: this.boldSpansOf({ text: itemText }), details: [] };
			if (isContinuingList) previousBlock.items.push(item);
			else blocks.push({ kind, items: [item], ...(kind === "numbered" ? { start: number } : {}) });
		};

		const lastListItem = () => {
			const previousBlock = blocks[blocks.length - 1];
			const isList = !!previousBlock && (previousBlock.kind === "numbered" || previousBlock.kind === "bullets");
			return isList && paragraphLines.length === 0 ? previousBlock.items[previousBlock.items.length - 1] : null;
		};

		lines.forEach(line => {
			const trimmedLine = line.trim();
			const isFence = trimmedLine.startsWith("```");
			if (fencedLines !== null) {
				if (isFence) {
					blocks.push({ kind: "fenced", text: fencedLines.join("\n") });
					fencedLines = null;
				} else {
					fencedLines.push(line);
				}
				return;
			}
			if (isFence) {
				closeParagraph();
				fencedLines = [];
				return;
			}
			if (!trimmedLine) {
				closeParagraph();
				return;
			}
			const headingMarks = [...trimmedLine].findIndex(character => character !== "#");
			const isHeading = headingMarks > 0 && trimmedLine.charAt(headingMarks) === " ";
			if (isHeading) {
				closeParagraph();
				const headingText = trimmedLine.slice(headingMarks).trim();
				blocks.push({ kind: "heading", level: Math.min(headingMarks, 3), spans: this.boldSpansOf({ text: headingText }) });
				return;
			}
			const isIndented = /^\s/.test(line);
			const isLettered = /^([a-z]|[ivx]+|\d+\.\d+)[.)]\s+/.test(trimmedLine);
			const itemForDetail = isIndented || isLettered ? lastListItem() : null;
			if (itemForDetail) {
				const detailText = isIndented && (trimmedLine.startsWith("- ") || trimmedLine.startsWith("* ")) ? trimmedLine.slice(2).trim() : trimmedLine;
				itemForDetail.details.push(this.boldSpansOf({ text: detailText }));
				return;
			}
			const isBullet = trimmedLine.startsWith("- ") || trimmedLine.startsWith("* ");
			if (isBullet) {
				closeParagraph();
				addListItem({ kind: "bullets", itemText: trimmedLine.slice(2).trim() });
				return;
			}
			const numberedMatch = trimmedLine.match(/^(\d+)[.)]\s+/);
			if (numberedMatch) {
				closeParagraph();
				addListItem({ kind: "numbered", itemText: trimmedLine.slice(numberedMatch[0].length).trim(), number: Number(numberedMatch[1]) });
				return;
			}
			paragraphLines.push(trimmedLine);
		});

		closeParagraph();
		if (fencedLines !== null) blocks.push({ kind: "fenced", text: fencedLines.join("\n") });
		return blocks;
	},

	/**
	 * A line of text split into plain and bold runs: text between a pair of "**" is bold. An
	 * unpaired "**" is kept as written.
	 *
	 * @param {Object} params
	 * @param {String} params.text
	 * @return {Array} [{ text, isBold }]
	 */
	boldSpansOf({ text }) {
		const pieces = String(text || "").split("**");
		const hasUnpairedMark = pieces.length % 2 === 0;
		if (hasUnpairedMark) return [{ text, isBold: false }];
		const spans = pieces.map((piece, index) => ({ text: piece, isBold: index % 2 === 1 }));
		const nonEmptySpans = spans.filter(span => span.text.length > 0);
		return nonEmptySpans;
	},
};

export default SkillTextService;
